import { useTranslation } from "react-i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { Button } from "wiz-front-shared/components/button/Button";
import { useQuery } from "hooks/useQuery";
import {
    recoverDataAtom,
    pushRecoverStepAtom,
    recoverHistoryDepthAtom,
    setRecoveryExiting,
} from "store/recoverStore";
import { AccountInfoBox } from "components/common/accountInfoBox/AccountInfoBox";
import './Recover.css';

// Step4: 결과 표시 (기존 Step3)
//   분기:
//   1. accountFound=false                  → 가입 이력 없음 → 회원가입 권유
//   2. provider=EMAIL  + recoveryToken     → 이메일 표시 + 비밀번호 재설정 버튼
//   3. provider=KAKAO/GOOGLE/...           → SNS 로그인 안내 + SignIn 이동 버튼
export const RecoverStep4 = () => {
    const { t } = useTranslation();
    const { navigate } = useQuery();
    const data = useAtomValue(recoverDataAtom);
    const pushStep = useSetAtom(pushRecoverStepAtom);
    const historyDepth = useAtomValue(recoverHistoryDepthAtom);

    // /signin 이동 — exit 모드 켜고 모든 /account-recovery history entry 를 한 번에 pop.
    //   마지막 pop 에서 URL 이 / 로 변경되며 /signin 마운트 (재마운트되는 /account-recovery 없음 → 깜빡임 없음).
    //   cascade 중 popstate handler 는 isExitingRecovery 플래그로 suppress.
    const goSignIn = () => {
        setRecoveryExiting(true);
        window.history.go(-(historyDepth + 1));
    };
    // 미가입 dead-end → /sign-up 으로 이동. device back 시 recovery 흐름 재진입 방지를 위해 플래그 기록.
    const goSignUp = () => {
        sessionStorage.setItem("__recoveryToSignup", "1");
        navigate("/sign-up");
    };

    // 0. find-account API 오류 — 일시적 장애. '미가입' 과 구분해 재시도 안내.
    if (data.findAccountFailed) {
        return (
            <div className="recover-step">
                <span className="title">{t("recover.step3.errorTitle")}</span>
                <span className="info">{t("recover.step3.errorInfo")}</span>
                <div className="recover-bottom-btn">
                    <Button
                        text={t("recover.step3.goSignInBtn")}
                        onPress={goSignIn}
                    />
                </div>
            </div>
        );
    }

    // 1. 가입 이력 없음
    if (!data.accountFound) {
        return (
            <div className="recover-step">
                <span className="title">{t("recover.step3.notFoundTitle")}</span>
                <span className="info">{t("recover.step3.notFoundInfo")}</span>
                <div className="recover-bottom-btn">
                    <Button
                        text={t("recover.step3.signUpBtn")}
                        onPress={goSignUp}
                    />
                </div>
            </div>
        );
    }

    const isEmail = data.foundProvider === "EMAIL";

    // 2. EMAIL — 이메일 표시 + 비밀번호 재설정
    if (isEmail) {
        return (
            <div className="recover-step">
                <span className="title">{t("recover.step3.foundTitle")}</span>
                <span className="info">{t("recover.step3.foundEmailInfo")}</span>

                <AccountInfoBox
                    provider="EMAIL"
                    email={data.foundEmail}
                    signedUpDate={data.foundSignedUpDate}
                />

                <div className="recover-bottom-btn recover-bottom-btn-stack">
                    <Button
                        text={t("recover.step3.resetPasswordBtn")}
                        onPress={() => pushStep(5)}
                        // recoveryToken 미발급 시 비밀번호 재설정 진입 차단 (Step5 에서 빈 토큰으로 호출되는 것 방지)
                        disabled={!data.recoveryToken}
                    />
                    <Button
                        text={t("recover.step3.goSignInBtn")}
                        onPress={goSignIn}
                        buttonColor="var(--lightGray)"
                        textColor="var(--black)"
                    />
                </div>
            </div>
        );
    }

    // 3. SNS — provider 알려주고 SignIn 이동
    return (
        <div className="recover-step">
            <span className="title">{t("recover.step3.foundTitle")}</span>
            <span className="info">
                {t("recover.step3.foundSnsInfo", { provider: t(data.foundProvider) })}
            </span>

            <AccountInfoBox
                provider={data.foundProvider || "EMAIL"}
                email={data.foundEmail}
                signedUpDate={data.foundSignedUpDate}
            />

            <div className="recover-bottom-btn">
                <Button
                    text={t("recover.step3.goSignInBtn")}
                    onPress={goSignIn}
                />
            </div>
        </div>
    );
};
